import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, object=authed
  const [savedIds, setSavedIds] = useState(new Set());

  const loadSaved = useCallback(async () => {
    try {
      const { data } = await api.get("/watchlist/ids");
      setSavedIds(new Set(data));
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setUser(r.data); if (r.data.role === "investor") loadSaved(); })
      .catch(() => setUser(false));
  }, [loadSaved]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    if (data.role === "investor") loadSaved();
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(false);
    setSavedIds(new Set());
  };

  const toggleSave = async (pitchId) => {
    const { data } = await api.post(`/watchlist/${pitchId}/toggle`);
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (data.saved) next.add(pitchId); else next.delete(pitchId);
      return next;
    });
    return data.saved;
  };

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, savedIds, toggleSave, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
